import { AlertTriangle, CheckCircle, Cloud, Server, Shield, User, UserCog, XCircle } from "lucide-react";
import { Badge } from "../../../ui/common-ui/badge";
import { Separator } from "../../../ui/common-ui/separator";
import { GraphNode } from "../types/graph";

interface EntityIdentityHeaderProps {
  node: GraphNode;
}

export function EntityIdentityHeader({ node }: EntityIdentityHeaderProps) {
  const props = node.props || {};
  const isServiceAccount = node.type === "service_account";
  const isAzure = props.cloudType === "AZURE";
  const isAws = props.cloudType === "AWS";

  const email = isServiceAccount
    ? String(props.serviceAccountEmail || props.name || props.roleArn || props.email || "")
    : String(props.email || props.userPrincipalName || "");

  const name = isServiceAccount
    ? isAws
      ? String(props.roleName || node.label)
      : isAzure
        ? String(props.name || node.label)
        : email.split("@")[0] || node.label
    : String(props.userName || props.displayName || props.username || node.label);

  const roles = Array.isArray(props.roles) ? props.roles : [];
  const roleCount = Number(props.roleCount || roles.length || 0);
  const resourceCount = Number(props.resourceCount || 0);
  const alertCount = Number(props.alertCount || 0);
  const isDormant = props.isDormant;
  const isUserManaged = props.isUserManaged;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center border ${
            isServiceAccount
              ? "bg-purple-500/10 border-purple-500/20"
              : "bg-primary/10 border-primary/20"
          }`}
        >
          {isServiceAccount ? (
            <Server className="w-6 h-6 text-purple-500" />
          ) : (
            <User className="w-6 h-6 text-primary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-foreground leading-tight truncate">{name}</h3>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {isDormant !== undefined && (
              <Badge
                variant="outline"
                className={`text-[10px] ${
                  isDormant
                    ? "border-destructive/40 bg-destructive/10 text-destructive"
                    : "border-primary/30 bg-primary/10 text-primary"
                }`}
              >
                {isDormant ? (
                  <>
                    <XCircle className="w-3 h-3 mr-1" />
                    Dormant
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Active
                  </>
                )}
              </Badge>
            )}
            {isServiceAccount && !isAzure && !isAws && isUserManaged !== undefined && (
              <Badge
                variant="outline"
                className={`text-[10px] ${
                  isUserManaged
                    ? "border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-400"
                    : "border-purple-500/40 bg-purple-500/10 text-purple-600 dark:text-purple-400"
                }`}
              >
                {isUserManaged ? (
                  <>
                    <UserCog className="w-3 h-3 mr-1" />
                    User Managed
                  </>
                ) : (
                  <>
                    <Cloud className="w-3 h-3 mr-1" />
                    Cloud Managed
                  </>
                )}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {email && <p className="break-all font-mono text-[11px] text-muted-foreground">{email}</p>}

      <div className="flex flex-wrap gap-2">
        {alertCount > 0 && (
          <Badge variant="outline" className="text-xs border-destructive/40 bg-destructive/10 text-destructive">
            <AlertTriangle className="w-3 h-3 mr-1" />
            {alertCount} Alert{alertCount !== 1 ? "s" : ""}
          </Badge>
        )}
        <Badge variant="outline" className="text-xs">
          <Shield className="w-3 h-3 mr-1" />
          {roleCount} Role{roleCount !== 1 ? "s" : ""}
        </Badge>
        <Badge variant="outline" className="text-xs">
          <Server className="w-3 h-3 mr-1" />
          {resourceCount} Resource{resourceCount !== 1 ? "s" : ""}
        </Badge>
      </div>

      <Separator className="bg-border/50" />
    </div>
  );
}
